import { Autocomplete, Chip, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { filter, find, first, includes, last, map, size, split } from 'lodash';
import React, { ReactElement } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { getStainTypeFilteredIds } from 'api/stainTypes';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { getStainKindChannelGroupLabel } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/channelSelection';
import ImpactIcon from 'components/StudyDashboard/QCMarkers/ImpactIcon';
import { getStainingMethodByStainType, StainingMethod } from 'interfaces/stainType';
import { humanize } from 'utils/helpers';
import {
  allChannelsOption,
  allIncludingExcludedChannelsOption,
  removeAllChannelsOption,
  useChannelsOptionsWithAllIncludingExcluded,
} from 'utils/queryHooks/useChannelsOptions';
import { CasesParams } from 'utils/useCasesParams/types';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import useStudy from 'utils/useStudy';
import { getSelectedChannels } from './Multiplex/helpers';

const CHANNEL_ACTION_OPTION_VALUES = [
  allChannelsOption.value,
  allIncludingExcludedChannelsOption.value,
  removeAllChannelsOption.value,
];

export interface ChannelsControllerProps {
  name: string;
  error: boolean;
  helperText?: string;
  methods: UseFormReturn<any, any>;
  additionalOnChange?: (value: string[]) => void;
  disabled?: boolean;
  casesParams: CasesParams;
  label?: string;
  placeholder?: string;
  selectAllOnLoad?: boolean;
  defaultSelectedChannelsOnLoad?: string[];
  allowedChannelNames?: string[];
  groupByStainKind?: boolean;
  singleSelection?: boolean;
  channelOptions?: Array<{ text: string; value: string }>;
}

export const ChannelsController = (props: ChannelsControllerProps): ReactElement => {
  const {
    name,
    methods,
    error,
    helperText,
    additionalOnChange,
    disabled = false,
    casesParams,
    label = 'Channels',
    placeholder = 'Select channels',
    selectAllOnLoad = false,
    defaultSelectedChannelsOnLoad,
    allowedChannelNames,
    groupByStainKind = false,
    singleSelection = false,
    channelOptions,
  } = props;
  const studyId = casesParams.filters?.studyId;
  const { stainTypeIdToDisplayName, stainTypeOptions, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();

  const isSpecificStudySelected = studyId && !includes(NON_SPECIFIC_STUDY_VALUES, studyId.toString());
  const { study } = useStudy(studyId, { enabled: isSpecificStudySelected });
  const qcMarkersStudySettings = study?.settings?.qcMarkersSettings;

  const { control, register, watch, setValue } = methods;
  const currentChannels = watch(name) || [];
  const hasAppliedAutoSelectOnLoad = React.useRef(false);

  const { data: stains } = useQuery({
    queryKey: ['slidesStainsTypes', casesParams],
    queryFn: ({ signal }) => getStainTypeFilteredIds(casesParams, signal),
  });

  const oneRelevantStain = first(stains);
  const splittedStains = includes(oneRelevantStain, '_') ? split(oneRelevantStain, '_') : [oneRelevantStain];
  const stainingMethod = getStainingMethodByStainType(oneRelevantStain);
  const stainOptions = map(splittedStains, (stain) => {
    return {
      text: stainTypeIdToDisplayName(stain),
      value: stain,
    };
  });

  const { channelMarkersOptions, channelModalities, isLoadingChannelMarkers } =
    useChannelsOptionsWithAllIncludingExcluded({
      casesParams,
      currentChannels,
    });
  const resolvedChannelMarkerOptions = channelOptions ?? channelMarkersOptions;
  const usesChannelOptions = channelOptions !== undefined || stainingMethod === StainingMethod.mplex;
  const isLoadingChannels = channelOptions === undefined && (isLoadingChannelMarkers || isLoadingStainTypeOptions);

  const filteredChannelMarkersOptions = React.useMemo(() => {
    if (!usesChannelOptions || allowedChannelNames == null) {
      return resolvedChannelMarkerOptions;
    }

    return filter(resolvedChannelMarkerOptions, ({ value }) => includes(allowedChannelNames, value));
  }, [allowedChannelNames, resolvedChannelMarkerOptions, usesChannelOptions]);

  const channelsOptions = React.useMemo(() => {
    if (!usesChannelOptions) {
      return stainOptions;
    }

    if (size(filteredChannelMarkersOptions) === 0) {
      return [];
    }

    if (singleSelection) {
      return filteredChannelMarkersOptions;
    }

    return size(currentChannels) > 0
      ? [removeAllChannelsOption, ...filteredChannelMarkersOptions]
      : [allChannelsOption, allIncludingExcludedChannelsOption, ...filteredChannelMarkersOptions];
  }, [currentChannels, filteredChannelMarkersOptions, singleSelection, stainOptions, usesChannelOptions]);

  const filteredDefaultSelectedChannelsOnLoad = React.useMemo(() => {
    if (size(defaultSelectedChannelsOnLoad) === 0 || allowedChannelNames == null) {
      return defaultSelectedChannelsOnLoad;
    }

    return filter(defaultSelectedChannelsOnLoad, (channelName) => includes(allowedChannelNames, channelName));
  }, [allowedChannelNames, defaultSelectedChannelsOnLoad]);

  React.useEffect(() => {
    if ((selectAllOnLoad || size(filteredDefaultSelectedChannelsOnLoad) > 0) && size(currentChannels) > 0) {
      hasAppliedAutoSelectOnLoad.current = true;
    }
  }, [currentChannels, filteredDefaultSelectedChannelsOnLoad, selectAllOnLoad]);

  React.useEffect(() => {
    if (
      (!selectAllOnLoad && size(filteredDefaultSelectedChannelsOnLoad) === 0) ||
      disabled ||
      hasAppliedAutoSelectOnLoad.current ||
      isLoadingChannels ||
      size(currentChannels) > 0
    ) {
      return;
    }

    const autoSelectedChannels = size(filteredDefaultSelectedChannelsOnLoad)
      ? filteredDefaultSelectedChannelsOnLoad
      : usesChannelOptions
      ? getSelectedChannels([allChannelsOption], filteredChannelMarkersOptions, qcMarkersStudySettings)
      : map(stainOptions, 'value');

    if (size(autoSelectedChannels) === 0 || (size(autoSelectedChannels) === 1 && !first(autoSelectedChannels))) {
      return;
    }

    hasAppliedAutoSelectOnLoad.current = true;
    setValue(name, autoSelectedChannels, { shouldValidate: true });
    additionalOnChange?.(autoSelectedChannels);
  }, [
    additionalOnChange,
    currentChannels,
    disabled,
    filteredChannelMarkersOptions,
    filteredDefaultSelectedChannelsOnLoad,
    isLoadingChannels,
    name,
    qcMarkersStudySettings,
    selectAllOnLoad,
    setValue,
    stainOptions,
    usesChannelOptions,
  ]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          disabled={disabled || isLoadingChannels}
          {...register(name)}
          multiple
          options={channelsOptions}
          groupBy={
            groupByStainKind
              ? (option) =>
                  includes(CHANNEL_ACTION_OPTION_VALUES, option.value)
                    ? ''
                    : getStainKindChannelGroupLabel(option.value, stainTypeOptions, channelModalities)
              : undefined
          }
          renderGroup={
            groupByStainKind
              ? (params) => (
                  <li key={params.key}>
                    {params.group ? (
                      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', px: 2, pt: 1 }}>
                        {params.group}
                      </Typography>
                    ) : null}
                    <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                  </li>
                )
              : undefined
          }
          getOptionLabel={(option) => option.text}
          value={map(value || [], (val: string) => {
            return find(channelsOptions, (opt) => opt.value === val) || { text: val, value: val };
          })}
          onChange={(event, newValue) => {
            event.stopPropagation();
            let selectedChannels: string[];
            if (singleSelection) {
              selectedChannels = size(newValue) ? [last(map(newValue, 'value')) as string] : [];
            } else if (usesChannelOptions) {
              selectedChannels = getSelectedChannels(newValue, filteredChannelMarkersOptions, qcMarkersStudySettings);
            } else {
              selectedChannels = map(newValue, 'value');
            }
            onChange(selectedChannels);
            if (additionalOnChange) additionalOnChange(selectedChannels);
          }}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          loading={isLoadingChannels}
          disableCloseOnSelect={!singleSelection}
          renderOption={(optionProps, option) => {
            return (
              <li {...optionProps} key={option.value}>
                <ImpactIcon qcImpactId={qcMarkersStudySettings?.[option.value] || ''} emptyIconPlaceholder={true} />
                {option.text}
              </li>
            );
          }}
          renderTags={(tagValue, getTagProps) =>
            map(tagValue, (option, index) => (
              <Chip
                key={option.value}
                label={
                  <Grid container alignContent="center">
                    <Grid item>
                      <ImpactIcon qcImpactId={qcMarkersStudySettings?.[option.value] || ''} />
                    </Grid>
                    <Grid item>{option.text}</Grid>
                  </Grid>
                }
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              required
              error={error}
              helperText={humanize(helperText)}
              disabled={isLoadingChannels}
              onClick={(event) => {
                event.stopPropagation();
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoadingChannels ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};
